import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { FiExternalLink, FiMenu, FiSearch, FiX } from 'react-icons/fi';
import { cCategories, cSidebar, fullUrl } from '../../api';
import { useLanguage } from '../../i18n';
import { BLUE } from './brand';

const ROW = 'flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition';
const ICON = 'w-8 h-8 rounded-lg flex items-center justify-center text-lg shrink-0 bg-gray-100 dark:bg-gray-800';

/**
 * The **hamburger menu** of the top bar: every category of the community in one
 * place — “ទាំងអស់” (All) first, then all categories with their article counts
 * and, underneath, the admin “General” menu (the same entries as the left
 * sidebar, which phones cannot see). A small search box finds an entry fast.
 */
export default function CategoryDrawer() {
  const { t, lang } = useLanguage();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [cats, setCats] = useState(null);        // null = not loaded yet
  const [menu, setMenu] = useState(null);

  // Load once, the first time the drawer is opened.
  useEffect(() => {
    if (!open) return;
    if (cats === null) cCategories().then((rows) => setCats(Array.isArray(rows) ? rows : [])).catch(() => setCats([]));
    if (menu === null) cSidebar().then((res) => setMenu(res?.items || [])).catch(() => setMenu([]));
  }, [open, cats, menu]);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const name = (row) => (lang === 'kh' ? (row.name_km || row.name) : row.name);
  const needle = term.trim().toLowerCase();
  const hit = (row) => !needle || name(row).toLowerCase().includes(needle);
  const list = (cats || []).filter(hit);
  const entries = (menu || []).filter(hit);

  // The admin menu is grouped by its main category, exactly like the sidebar.
  const groups = [];
  entries.forEach((item) => {
    const key = item.category ? `c${item.category.id}` : 'general';
    let group = groups.find((g) => g.key === key);
    if (!group) {
      group = {
        key,
        name: item.category
          ? (lang === 'kh' ? (item.category.name_km || item.category.name) : item.category.name)
          : t('generalMenu'),
        icon: item.category?.icon || '📁',
        items: [],
      };
      groups.push(group);
    }
    group.items.push(item);
  });

  const entry = (item) => {
    const label = lang === 'kh' ? (item.name_km || item.name) : item.name;
    const body = (
      <>
        {item.image ? (
          <img src={fullUrl(item.image)} alt={label} className="w-8 h-8 rounded-lg object-cover shrink-0" />
        ) : item.icon ? (
          <span className={ICON}>{item.icon}</span>
        ) : null}
        <span className="flex-1 min-w-0 truncate font-semibold">{label}</span>
        {item.link && item.new_tab ? <FiExternalLink className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : null}
      </>
    );
    const close = () => setOpen(false);
    if (!item.link) return <span key={item.id} className={ROW}>{body}</span>;
    if (/^https?:/i.test(item.link)) {
      return (
        <a key={item.id} href={item.link} onClick={close} className={ROW}
           target={item.new_tab ? '_blank' : undefined} rel={item.new_tab ? 'noreferrer' : undefined}>
          {body}
        </a>
      );
    }
    return <Link key={item.id} to={item.link} onClick={close} className={ROW}>{body}</Link>;
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={t('sideMenu')}
        aria-label={t('sideMenu')}
        className="w-9 h-9 rounded-xl flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 shrink-0"
      >
        <FiMenu className="w-5 h-5" />
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[70]">
          <div className="absolute inset-0 bg-black/40 anim-fade-in" onClick={() => setOpen(false)} />
          <aside className="relative w-[310px] max-w-[86vw] h-full bg-white dark:bg-gray-900 shadow-2xl overflow-y-auto anim-slide-right">
            <header className="sticky top-0 z-10 h-14 px-3 flex items-center gap-2 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-100 dark:border-gray-800">
              <img src={`${process.env.PUBLIC_URL}/ckl.png`} alt="CodeKhmerLearning" className="w-8 h-8" />
              <span className="flex-1 min-w-0 truncate font-extrabold text-sm">{t('sideMenu')}</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('close')}
                className="w-9 h-9 rounded-xl flex items-center justify-center text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <FiX className="w-5 h-5" />
              </button>
            </header>

            <div className="p-2 space-y-3">
              <label className="relative flex items-center gap-2 rounded-xl bg-gray-100 dark:bg-gray-800 px-3 py-2">
                <FiSearch className="w-4 h-4 text-gray-400" />
                <input
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder={t('search')}
                  className="flex-1 min-w-0 bg-transparent text-sm outline-none"
                />
              </label>

              {/* “ទាំងអស់” — every article, whatever its category */}
              <Link to="/" onClick={() => setOpen(false)} className={ROW} style={{ color: BLUE }}>
                <span className={ICON}>🌈</span>
                <span className="flex-1 font-extrabold">{t('allPosts')}</span>
              </Link>

              <section>
                <p className="px-2.5 pt-1 pb-1 text-[11px] font-extrabold uppercase tracking-wide text-gray-400">
                  {t('categoryPost')}
                </p>
                <div className="space-y-0.5">
                  {list.map((c) => (
                    <Link key={c.id} to={`/?category_id=${c.id}`} onClick={() => setOpen(false)} className={ROW}>
                      <span className={ICON}>{c.icon || '📁'}</span>
                      <span className="flex-1 min-w-0 truncate font-semibold">{name(c)}</span>
                      <span className="text-[11px] text-gray-400">{c.post_count ?? 0}</span>
                    </Link>
                  ))}
                  {cats !== null && list.length === 0 && <p className="px-2.5 py-1.5 text-xs text-gray-400">—</p>}
                </div>
              </section>

              {groups.length > 0 && (
                <section>
                  <p className="px-2.5 pt-1 pb-1 text-[11px] font-extrabold uppercase tracking-wide text-gray-400">
                    {t('generalMenu')}
                  </p>
                  {groups.map((g) => (
                    <div key={g.key} className="space-y-0.5">
                      <p className="flex items-center gap-2 px-2.5 py-1.5 text-[12px] font-extrabold text-gray-500">
                        <span>{g.icon}</span>
                        <span className="truncate">{g.name}</span>
                      </p>
                      {g.items.map(entry)}
                    </div>
                  ))}
                </section>
              )}
            </div>
          </aside>
        </div>,
        document.body,
      )}
    </>
  );
}

